<template>
  <div class="parent-child-multi-select">
    <div class="parent-select">
      <BaseElSelect
        v-model="selectedParents"
        :disabled="disabled"
        multiple
        clearable
        @change="onParentChange"
        @clear="onParentClear"
      >
        <BaseElSelectOption
          v-for="option in subFieldOptions"
          :key="option.parentValue"
          :label="option.parentValue"
          :value="option.parentValue"
        />
      </BaseElSelect>
    </div>
    <div class="child-select mt-2">
      <BaseElSelect
        v-model="selectedChildren"
        :disabled="disabled || !selectedParents.length"
        multiple
        clearable
        @change="onChildChange"
        @clear="onChildClear"
      >
        <el-option-group
          v-for="parentGroup in groupedChildOptions"
          :key="parentGroup.label"
          :label="parentGroup.label"
        >
          <BaseElSelectOption
            v-for="child in parentGroup.options"
            :key="child"
            :label="child"
            :value="child"
          />
        </el-option-group>
      </BaseElSelect>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'

export default defineComponent({
  name: 'ParentChildMultiSelect',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    subFieldOptions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    parentPlaceholder: {
      type: String,
      default: '',
    },
    childPlaceholder: {
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'change'],
  setup (props, { emit }) {
    const selectedParents = ref([])
    const selectedChildren = ref([])

    // 計算可用的子選項（基於選中的父選項）
    const availableChildOptions = computed(() => {
      if (!selectedParents.value.length) return []

      const allChildOptions = []
      selectedParents.value.forEach(parentValue => {
        const parentOption = props.subFieldOptions.find(option => option.parentValue === parentValue)
        if (parentOption && parentOption.childOptions) {
          allChildOptions.push(...parentOption.childOptions)
        }
      })

      // 去重
      return [...new Set(allChildOptions)]
    })

    // 計算分組的子選項（根據父選項分組）
    const groupedChildOptions = computed(() => {
      if (!selectedParents.value.length) return []

      const groups = []
      selectedParents.value.forEach(parentValue => {
        const parentOption = props.subFieldOptions.find(option => option.parentValue === parentValue)
        if (parentOption && parentOption.childOptions && parentOption.childOptions.length > 0) {
          groups.push({
            label: parentValue,
            options: parentOption.childOptions,
          })
        }
      })

      return groups
    })

    // 生成輸出值
    const generateOutputValue = () => {
      const result = []

      selectedParents.value.forEach(parentValue => {
        const parentOption = props.subFieldOptions.find(option => option.parentValue === parentValue)
        if (parentOption && parentOption.childOptions) {
          parentOption.childOptions.forEach(childValue => {
            if (selectedChildren.value.includes(childValue)) {
              result.push({
                parentValue,
                childValue,
              })
            }
          })
        }
      })

      return result
    }

    const emitChange = () => {
      const value = generateOutputValue()
      emit('input', value)
      emit('change', value)
    }

    const onParentChange = () => {
      // 當父選項改變時，清理不再有效的子選項
      const validChildren = selectedChildren.value.filter(childValue => {
        return availableChildOptions.value.includes(childValue)
      })
      selectedChildren.value = validChildren
      emitChange()
    }

    const onParentClear = () => {
      selectedParents.value = []
      selectedChildren.value = []
      emitChange()
    }

    const onChildChange = () => {
      emitChange()
    }

    const onChildClear = () => {
      selectedChildren.value = []
      emitChange()
    }

    onMounted(() => {
      // 初始化時，如果有初始值，設置選中的父子選項
      if (props.value && Array.isArray(props.value) && props.value.length > 0) {
        const parents = []
        const children = []

        props.value.forEach(item => {
          const parentValue = item.parentValue || ''
          const childValue = item.childValue || ''

          if (parentValue && !parents.includes(parentValue)) {
            parents.push(parentValue)
          }
          if (childValue && !children.includes(childValue)) {
            children.push(childValue)
          }
        })

        selectedParents.value = parents
        selectedChildren.value = children
      }
    })

    return {
      selectedParents,
      selectedChildren,
      availableChildOptions,
      groupedChildOptions,
      onParentChange,
      onParentClear,
      onChildChange,
      onChildClear,
    }
  },
})
</script>

<style lang="postcss" scoped>
::v-deep .el-select {
  height: auto;
}
::v-deep .el-input {
  height: auto;
}
.parent-child-multi-select {
  @apply w-full;
}

.selected-item {
  @apply border border-gray-200 rounded p-3;
}

.parent-select,
.child-select {
  @apply w-full;
}
</style>
